"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { Download, X, Share, PlusSquare, Smartphone, CheckCircle } from "lucide-react";
import { usePathname } from "next/navigation";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function PwaInstallBanner() {
  const pathname = usePathname();
  if (pathname?.startsWith("/crm")) return null;

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    // 1. Check if already installed in localStorage
    if (localStorage.getItem("johnson_pwa_installed") === "true") {
      setDismissed(true);
      setIsInstallable(false);
      return;
    }

    // 2. Check if currently running in standalone mode (Installed PWA runtime)
    const isStandalone = 
      window.matchMedia("(display-mode: standalone)").matches || 
      (window.navigator as any).standalone === true ||
      (document.referrer && document.referrer.includes("android-app://"));

    if (isStandalone) {
      localStorage.setItem("johnson_pwa_installed", "true");
      setDismissed(true);
      setIsInstallable(false);
      return;
    }

    // 3. Listen for browser native 'appinstalled' event
    const handleAppInstalled = () => {
      localStorage.setItem("johnson_pwa_installed", "true");
      setDismissed(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", handleAppInstalled);

    // 4. Check if user dismissed recently (30 days cool-off)
    const dismissedTime = localStorage.getItem("johnson_pwa_dismissed");
    if (dismissedTime && Date.now() - parseInt(dismissedTime, 10) < 30 * 24 * 60 * 60 * 1000) {
      setDismissed(true);
      return;
    }

    // 5. Detect iOS Safari
    const ua = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua);
    setIsIos(isIosDevice);

    if (isIosDevice) {
      setIsInstallable(true);
      setDismissed(false);
    }

    // 6. Android/Desktop Chrome install prompt listener
    const handleBeforeInstall = (e: Event) => {
      if (localStorage.getItem("johnson_pwa_installed") === "true") {
        return;
      }
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
      setDismissed(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIos) {
      setShowIosModal(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem("johnson_pwa_installed", "true");
      setDismissed(true);
      setIsInstallable(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("johnson_pwa_dismissed", Date.now().toString());
  };

  const handleIosModalClose = (markInstalled = false) => {
    setShowIosModal(false);
    if (markInstalled) {
      localStorage.setItem("johnson_pwa_installed", "true");
      setDismissed(true);
      setIsInstallable(false);
    }
  };

  if (dismissed || !isInstallable) return null;

  return (
    <>
      {/* Mobile Floating Install Bar */}
      <div className="fixed bottom-4 left-3 right-3 sm:left-auto sm:right-6 sm:max-w-md z-50 animate-in fade-in slide-in-from-bottom-5 duration-300">
        <div className="rounded-2xl p-3.5 bg-slate-900/95 backdrop-blur-xl border border-cyan-500/30 shadow-2xl shadow-cyan-950/60 flex items-center justify-between gap-3">
          {/* App Icon + Text */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative w-11 h-11 rounded-xl overflow-hidden shadow-md border border-cyan-400/30 shrink-0 bg-[#0B132B]">
              <img
                src="/brand/app-icon.svg?v=3"
                alt="Johnson Softwares CRM"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h4 className="font-heading font-black text-xs text-white tracking-[0.12em] uppercase truncate">
                  JOHNSON <span className="text-cyan-400 tracking-[0.14em]">SOFTWARES</span> CRM
                </h4>
                <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shrink-0">
                  PWA
                </span>
              </div>
              <p className="text-[11px] text-slate-400 truncate">
                1-tap mobile access for field reps
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleInstallClick}
              className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-bold text-xs shadow-md shadow-cyan-500/20 flex items-center gap-1.5 active:scale-95 transition-all"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Install</span>
            </button>

            <button
              onClick={handleDismiss}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* iOS Safari "Add to Home Screen" Instructions Modal */}
      {showIosModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-sm rounded-3xl bg-slate-900 border border-slate-800 p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">Add to Home Screen</h3>
                  <p className="text-[11px] text-slate-400">Install in 2 taps on iOS Safari</p>
                </div>
              </div>
              <button
                onClick={() => setShowIosModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs text-slate-300">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-950/80 border border-slate-800/80">
                <div className="w-7 h-7 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                  <Share className="w-4 h-4" />
                </div>
                <span>1. Tap the <strong>Share</strong> button at the bottom of Safari.</span>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-950/80 border border-slate-800/80">
                <div className="w-7 h-7 rounded-lg bg-cyan-500/20 text-cyan-400 flex items-center justify-center shrink-0">
                  <PlusSquare className="w-4 h-4" />
                </div>
                <span>2. Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong>.</span>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-950/80 border border-slate-800/80">
                <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                  <CheckCircle className="w-4 h-4" />
                </div>
                <span>3. Tap <strong>Add</strong> to launch the CRM in full-screen native mode!</span>
              </div>
            </div>

            <button
              onClick={() => handleIosModalClose(true)}
              className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs transition-colors"
            >
              Got it, Don&apos;t Show Again
            </button>
          </div>
        </div>
      )}
    </>
  );
}
